import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { useAppStore } from "@/store";
import { downloadPathFor } from "@/lib/document-source";
import { documentRepoKeys } from "./use-document-repo";

type Document = components["schemas"]["Document"];

// Binaries are immutable post-upload — cache the blob indefinitely within the
// gc window; the object URL is per-hook-instance and revoked on unmount.
const BLOB_STALE_TIME = Infinity;
const BLOB_GC_TIME = 10 * 60_000;

const blobKey = (orgId: string, source: string, id: string, ctx: string) =>
  ["document-repo", "blob", orgId, source, id, ctx] as const;

export interface DocumentBlobResult {
  /** Renderable object URL for the fetched binary; null until loaded. */
  objectUrl: string | null;
  isPending: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Fetch one repository document's binary through the AUTHENTICATED API proxy
 * (`parseAs: "blob"` — the Bearer token rides the interceptor; a raw `<a href>`
 * to the API would 401). The download path is source-routed; of the six
 * sources (document/form/task/notice/cromerr/report_form_output), only cromerr
 * has no Document-reachable binary and is skipped (objectUrl stays null — the
 * drawer shows "Download to view" + a jump-to-source link).
 */
export function useDocumentBlob(doc: Document | null): DocumentBlobResult {
  const orgId = useAppStore((s) => s.activeOrgId);
  const pathTemplate = doc ? downloadPathFor(doc) : null;
  const enabled = Boolean(orgId) && Boolean(doc) && Boolean(pathTemplate);

  const query = useQuery<Blob>({
    queryKey: blobKey(orgId ?? "", doc?.source ?? "", doc?.id ?? "", doc?.context_id ?? ""),
    queryFn: async () => {
      if (!orgId || !doc) throw new Error("No document context");
      const { data, error, response } = await fetchDocumentBlob(orgId, doc);
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to download document";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      // parseAs: "blob" returns a runtime Blob; the generated type is string.
      return data as unknown as Blob;
    },
    enabled,
    staleTime: BLOB_STALE_TIME,
    gcTime: BLOB_GC_TIME,
  });

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!query.data) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(query.data);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [query.data]);

  return {
    objectUrl,
    isPending: enabled && query.isPending,
    isError: query.isError,
    error: query.error,
  };
}

/**
 * Dispatch the authenticated GET for a document's binary to the source-specific
 * download operation. openapi-fetch needs the literal path string per operation,
 * so the switch maps source → typed call (all `parseAs: "blob"`).
 */
function fetchDocumentBlob(orgId: string, doc: Document) {
  switch (doc.source) {
    case "document":
      return apiClient.GET("/orgs/{orgId}/entity-attachments/{attachmentId}/download", {
        params: { path: { orgId, attachmentId: doc.id } },
        parseAs: "blob",
      });
    case "form":
      return apiClient.GET("/orgs/{orgId}/form-submissions/attachments/{attachmentId}/download", {
        params: { path: { orgId, attachmentId: doc.id } },
        parseAs: "blob",
      });
    case "task":
      return apiClient.GET("/orgs/{orgId}/tasks/{taskId}/attachments/{attachmentId}/download", {
        params: { path: { orgId, taskId: doc.context_id ?? "", attachmentId: doc.id } },
        parseAs: "blob",
      });
    case "notice":
      return apiClient.GET("/orgs/{orgId}/notices/{id}/pdf", {
        params: { path: { orgId, id: doc.id } },
        parseAs: "blob",
      });
    case "report_form_output":
      return apiClient.GET("/orgs/{orgId}/report-form-outputs/{outputId}/pdf", {
        params: { path: { orgId, outputId: doc.id } },
        parseAs: "blob",
      });
    default:
      // Unreachable: useDocumentBlob disables the query for non-downloadable
      // sources via downloadPathFor returning null.
      throw new Error(`Source ${doc.source} has no inline download`);
  }
}

/**
 * Source-aware delete. Routes a repository document to the DELETE endpoint owned
 * by its source: document → entity-attachment, form → form-submission
 * attachment, task → task attachment (keyed by context_id = task id). Protected
 * sources (notice, cromerr, report_form_output) have NO delete endpoint and MUST
 * be filtered out at the call site (the dialog disables their trash icon). On
 * success the repo list (and its roll-up counts) is invalidated.
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (doc: Document) => {
      if (!orgId) throw new Error("No org context");
      const result = await deleteForSource(orgId, doc);
      if (result.error) {
        const msg = (result.error as { error?: string }).error ?? "Failed to delete document";
        throw new ApiError(
          result.response.status,
          msg,
          (result.error as { code?: string }).code,
          result.error,
        );
      }
      return result.data;
    },
    onSuccess: () => {
      if (!orgId) return;
      void queryClient.invalidateQueries({ queryKey: documentRepoKeys.lists(orgId) });
    },
  });
}

function deleteForSource(orgId: string, doc: Document) {
  switch (doc.source) {
    case "document":
      return apiClient.DELETE("/orgs/{orgId}/entity-attachments/{attachmentId}", {
        params: { path: { orgId, attachmentId: doc.id } },
      });
    case "form":
      return apiClient.DELETE("/orgs/{orgId}/form-submissions/attachments/{attachmentId}", {
        params: { path: { orgId, attachmentId: doc.id } },
      });
    case "task":
      return apiClient.DELETE("/orgs/{orgId}/tasks/{taskId}/attachments/{attachmentId}", {
        params: { path: { orgId, taskId: doc.context_id ?? "", attachmentId: doc.id } },
      });
    default:
      // notice / cromerr / report_form_output are protected — the UI never reaches here.
      throw new Error(`Source ${doc.source} is protected and cannot be deleted`);
  }
}
